import api from '../baseAPIs/shopify';
import type { AxiosError } from 'axios';
import type {
    StoreLinkLinkExpiredResponse,
    StoreLinkVerifyResponse,
    StoreLinkVerifyResult
} from '@extTypes/shoplazza/storeLink';

export const confirmSubscription = async (
    store: string,
    chargeId: string,
    timestamp: string,
    nonce: string,
    key: string,
    storeType = 'shopify',
    collectiveRetail?: string
): Promise<{
    redirectUrl: string;
}> => {
    const response = await api.post(`payment/${storeType}/${store}/confirmed`, undefined, {
        params: {
            chargeId: chargeId,
            timestamp: timestamp,
            nonce: nonce,
            key: key,
            collectiveRetail: collectiveRetail
        }
    });
    return response.data;
};

export const forceStoreResubscribe = async (store: string, sendEmail = true): Promise<void> => {
    await api.post('payment/force-resubscribe', undefined, {
        params: { shop: store, sendEmail }
    });
};

export const getApplicationCharge = async (
    store: string,
    key: string
): Promise<{
    charge_id: string;
}> => {
    const response = await api.get(`payment/shoplazza/${store}/applicationCharge`, {
        params: {
            hash: key
        }
    });
    return response.data;
};

/** The signed link from the subscription redirect expired (120 minutes) or is invalid */
export const isLinkExpired = (error: AxiosError): boolean =>
    error.response?.status === 403 &&
    (error.response.data as Partial<StoreLinkLinkExpiredResponse> | undefined)?.code === 'LinkExpired';

/** Verify outcome, plus the signed link from the subscription redirect having expired (120 minutes) */
export type ShoplazzaStoreLinkVerifyResult = StoreLinkVerifyResult | 'link-expired';

/** Links the Shoplazza store to the account the code was emailed to (signed params from the subscription redirect) */
export const verifyShoplazzaStoreLink = async (
    store: string,
    code: string,
    timestamp: string,
    nonce: string,
    key: string
): Promise<ShoplazzaStoreLinkVerifyResult> => {
    try {
        const response = await api.post<StoreLinkVerifyResponse>(
            `payment/shoplazza/${store}/storeLink/verify`,
            { code },
            { params: { timestamp, nonce, key } }
        );
        return response.data.result;
    } catch (error_) {
        const error = error_ as AxiosError;
        // Wrong or expired codes come back as 400 with the reason (reading a property of a text body gives undefined)
        const result = (error.response?.data as Partial<StoreLinkVerifyResponse> | undefined)?.result;
        if (error.response?.status === 400 && result) return result;
        if (isLinkExpired(error)) return 'link-expired';
        throw error;
    }
};
